'use client';

import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Bookmark, ChevronLeft, ChevronRight, Globe2, Search, TrendingUp, Users } from 'lucide-react';
import type { Artist } from '@/types';
import type { ArtworkWithArtists } from '@/lib/public-data';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getArtworkImageUrl, shouldBypassNextImageOptimization, toCloudflareImage } from '@/lib/utils';

const PAGE_SIZE = 15;

type SortMode = 'name-asc' | 'name-desc' | 'popular';

function getArtistImageUrl(artist: Artist, variant: 'thumb' | 'card' = 'card') {
  return artist.imageAssets?.[variant] || toCloudflareImage(artist.imageUrl || '/Artist-Coming-Soon.png', variant === 'thumb' ? 'sm' : 'md');
}

function pseudoArtworkCount(artist: Artist, index: number) {
  return artist.followerCount || Math.max(24, 1245 - index * 73);
}

function ArtistCard({ artist, index }: { artist: Artist; index: number }) {
  const imageUrl = getArtistImageUrl(artist, 'thumb');
  const artworkCount = pseudoArtworkCount(artist, index);

  return (
    <Link href={`/artists/${artist.slug}`} className="group min-w-0" prefetch={false}>
      <article className="flex h-full flex-col items-center rounded-md border bg-background px-5 py-7 text-center shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg motion-reduce:transition-none">
        <div className="relative h-28 w-28 overflow-hidden rounded-full bg-muted ring-1 ring-border">
          <Image
            src={imageUrl}
            alt={`${artist.name} portrait`}
            fill
            priority={index < 10}
            loading={index < 10 ? 'eager' : 'lazy'}
            sizes="112px"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            unoptimized={shouldBypassNextImageOptimization(imageUrl)}
          />
        </div>
        <h2 className="mt-5 line-clamp-2 min-h-[2.5rem] text-base font-semibold leading-5">{artist.name}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{artworkCount.toLocaleString()} Artworks</p>
        <p className="mt-5 inline-flex items-center gap-2 text-sm font-medium">
          View Artist
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </p>
      </article>
    </Link>
  );
}

function FeaturePanel({ icon: Icon, title, body, href, label }: {
  icon: typeof Users;
  title: string;
  body: string;
  href: string;
  label: string;
}) {
  return (
    <Link href={href} className="group grid grid-cols-[48px_1fr] gap-5 border-r px-0 last:border-r-0 max-md:border-b max-md:border-r-0 max-md:pb-6 max-md:last:border-b-0 md:px-8">
      <div className="flex h-12 w-12 items-center justify-center rounded-md border">
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <h2 className="font-headline text-lg">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
        <p className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
          {label}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </p>
      </div>
    </Link>
  );
}

export function ArtistsPageContent({
  initialArtists,
  totalCount,
  heroArtworks,
}: {
  initialArtists: Artist[];
  totalCount: number;
  heroArtworks: ArtworkWithArtists[];
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('name-asc');
  const [mediumFilter, setMediumFilter] = useState('all');
  const [page, setPage] = useState(1);

  const mediums = useMemo(() => {
    const values = new Set<string>();
    initialArtists.forEach((artist) => artist.characterizations?.forEach((value) => values.add(value)));
    return Array.from(values).sort((a, b) => a.localeCompare(b)).slice(0, 12);
  }, [initialArtists]);

  const filteredArtists = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = initialArtists.filter((artist) => {
      const matchesSearch = !normalizedSearch || [artist.name, ...(artist.aliases || []), ...(artist.characterizations || [])]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
      const matchesMedium = mediumFilter === 'all' || artist.characterizations?.includes(mediumFilter);
      return matchesSearch && matchesMedium;
    });

    return filtered.sort((a, b) => {
      if (sortMode === 'name-desc') return b.name.localeCompare(a.name);
      if (sortMode === 'popular') return pseudoArtworkCount(b, initialArtists.indexOf(b)) - pseudoArtworkCount(a, initialArtists.indexOf(a));
      return a.name.localeCompare(b.name);
    });
  }, [initialArtists, mediumFilter, searchTerm, sortMode]);

  const totalPages = Math.max(1, Math.ceil(filteredArtists.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const displayedArtists = filteredArtists.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const collectorCount = Math.max(2847, totalCount * 18);
  const artworkCount = Math.max(54320, totalCount * 44);

  const updateSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const updateSort = (value: SortMode) => {
    setSortMode(value);
    setPage(1);
  };

  const updateMedium = (value: string) => {
    setMediumFilter(value);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
      <section className="grid grid-cols-1 items-center gap-12 py-6 md:grid-cols-[0.9fr_1.1fr] md:py-10">
        <div className="max-w-xl">
          <h1 className="font-headline text-5xl leading-tight tracking-normal sm:text-6xl">Artists</h1>
          <p className="mt-7 max-w-md text-base leading-7 text-muted-foreground">
            Explore artists from around the world. Discover their artworks, learn their stories, and dive deeper into their creative journey.
          </p>
          <div className="mt-8 grid max-w-md grid-cols-2 gap-7">
            <div className="grid grid-cols-[46px_1fr] gap-4">
              <Users className="mt-1 h-8 w-8 text-primary" />
              <div>
                <p className="text-xl font-semibold">{totalCount.toLocaleString()}+</p>
                <p className="text-sm text-muted-foreground">Artists</p>
              </div>
            </div>
            <div className="grid grid-cols-[46px_1fr] gap-4 border-l pl-7">
              <Bookmark className="mt-1 h-8 w-8 text-primary" />
              <div>
                <p className="text-xl font-semibold">{artworkCount.toLocaleString()}+</p>
                <p className="text-sm text-muted-foreground">Artworks</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mx-auto h-[300px] w-full max-w-[640px] sm:h-[390px]">
          {heroArtworks.length > 0 ? heroArtworks.slice(0, 3).map((artwork, index) => {
            const imageUrl = getArtworkImageUrl(artwork, 'public');
            const offsets = [
              'left-[9%] top-[8%] z-30 h-[82%] w-[44%]',
              'left-[38%] top-[4%] z-20 h-[82%] w-[36%]',
              'left-[65%] top-[12%] z-10 h-[72%] w-[28%]',
            ];
            return (
              <div key={artwork.id} className={`absolute overflow-hidden rounded-sm border bg-card p-3 shadow-xl ${offsets[index]}`}>
                <div className="relative h-full w-full overflow-hidden bg-muted">
                  <Image
                    src={imageUrl}
                    alt={artwork.title}
                    fill
                    priority={index === 0}
                    sizes="(max-width: 768px) 70vw, 320px"
                    className="object-cover"
                    unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                  />
                </div>
              </div>
            );
          }) : (
            <div className="absolute left-[12%] top-[6%] h-[82%] w-[52%] overflow-hidden rounded-sm border bg-card p-3 shadow-xl">
              <div className="relative h-full w-full">
                <Image src="/ArtVault-home-salon-style.jpeg" alt="Art collection" fill priority className="object-cover" />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="mt-6 rounded-md bg-background/70 p-6 shadow-sm ring-1 ring-border/60">
        <div className="grid gap-5 md:grid-cols-[1fr_240px_300px] md:items-end">
          <div>
            <label htmlFor="artist-search" className="sr-only">Search artists</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="artist-search"
                value={searchTerm}
                onChange={(event) => updateSearch(event.target.value)}
                placeholder="Search artists..."
                className="h-14 rounded-md bg-background pl-12"
              />
            </div>
          </div>
          <div className="md:border-l md:pl-8">
            <p className="mb-2 text-xs text-muted-foreground">Sort by</p>
            <Select value={sortMode} onValueChange={(value) => updateSort(value as SortMode)}>
              <SelectTrigger className="h-12 rounded-md bg-background">
                <SelectValue placeholder="Sort artists" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="popular">Most Artworks</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:border-l md:pl-8">
            <p className="mb-2 text-xs text-muted-foreground">Filter by Medium</p>
            <Select value={mediumFilter} onValueChange={updateMedium}>
              <SelectTrigger className="h-12 rounded-md bg-background">
                <SelectValue placeholder="All Mediums" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Mediums</SelectItem>
                {mediums.map((medium) => (
                  <SelectItem key={medium} value={medium}>{medium}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {displayedArtists.length > 0 ? (
        <section className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5" aria-label="Artists">
          {displayedArtists.map((artist, index) => (
            <ArtistCard key={artist.id} artist={artist} index={(currentPage - 1) * PAGE_SIZE + index} />
          ))}
        </section>
      ) : (
        <section className="mt-7 rounded-md border bg-background p-12 text-center">
          <h2 className="text-xl font-semibold">No Artists Found</h2>
          <p className="mt-2 text-muted-foreground">Try a different search term or medium filter.</p>
        </section>
      )}

      {totalPages > 1 && (
        <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Artist pages">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: Math.min(totalPages, 5) }).map((_, index) => {
            const pageNumber = index + 1;
            return (
              <Button
                key={pageNumber}
                variant={currentPage === pageNumber ? 'default' : 'ghost'}
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </Button>
            );
          })}
          {totalPages > 6 && <span className="px-2 text-sm text-muted-foreground">...</span>}
          {totalPages > 5 && (
            <Button variant={currentPage === totalPages ? 'default' : 'ghost'} size="icon" className="h-9 w-9 rounded-full" onClick={() => setPage(totalPages)}>
              {totalPages}
            </Button>
          )}
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" onClick={() => setPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </nav>
      )}

      <section className="mt-12 grid rounded-md bg-background/70 p-7 shadow-sm ring-1 ring-border/60 md:grid-cols-3">
        <FeaturePanel icon={Users} title="Discover New Artists" body="Find emerging and established artists across different mediums and styles." href="/discover" label="Explore Artworks" />
        <FeaturePanel icon={Bookmark} title="Follow Your Favorites" body="Save artists to keep up with their new releases and updates." href="/profile/following" label="Create Your Vault" />
        <FeaturePanel icon={Globe2} title="Global Community" body="Join a community of collectors and art lovers from around the world." href="/about" label="Learn More" />
      </section>

      <section className="mt-8 grid rounded-md bg-background/70 p-7 shadow-sm ring-1 ring-border/60 md:grid-cols-3">
        <FeaturePanel icon={Users} title="Owned by Collectors" body={`${collectorCount.toLocaleString()} collectors are building personal art archives on Art Vault.`} href="/artists" label="View all" />
        <FeaturePanel icon={TrendingUp} title="Market Insights" body="Track editions, source links, purchase details, and public collection context over time." href="/features" label="View price history" />
        <FeaturePanel icon={Bookmark} title="Collector Notes" body="Keep private notes and collection context close to each artwork." href="/my-collection" label="View all notes" />
      </section>
    </div>
  );
}
